import React, { createContext, useContext, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CONTENT } from "./content";

const LanguageContext = createContext(null);
const STORAGE_KEY = "slm_lang";
const SUPPORTED = ["en", "es"];

function detectInitialLang() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED.includes(saved)) return saved;
    const browser = (navigator.language || navigator.userLanguage || "en").slice(0, 2).toLowerCase();
    if (SUPPORTED.includes(browser)) return browser;
  } catch (e) {
    /* ignore */
  }
  return "en";
}

export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const detected = useRef(false);
  const lang = location.pathname === "/es" || location.pathname.startsWith("/es/") ? "es" : "en";

  const pathFor = useCallback((path, targetLang = lang) => {
    const normalized = path === "/es" ? "/" : path.replace(/^\/es(?=\/)/, "") || "/";
    return targetLang === "es" ? (normalized === "/" ? "/es" : `/es${normalized}`) : normalized;
  }, [lang]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch (e) { /* ignore */ }
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    if (detected.current || location.pathname !== "/") return;
    detected.current = true;
    if (detectInitialLang() === "es") navigate("/es", { replace: true });
  }, [location.pathname, navigate]);

  const setLang = useCallback((l) => {
    if (!SUPPORTED.includes(l) || l === lang) return;
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch (e) { /* ignore */ }
    navigate(`${pathFor(location.pathname, l)}${location.search}${location.hash}`);
  }, [lang, location.hash, location.pathname, location.search, navigate, pathFor]);

  const toggleLang = useCallback(() => {
    setLang(lang === "en" ? "es" : "en");
  }, [lang, setLang]);

  // t("about.title") -> string, falls back to EN then key
  const t = useCallback(
    (path) => {
      const read = (obj) => path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
      const val = read(CONTENT[lang]);
      if (val !== undefined && val !== null) return val;
      const fallback = read(CONTENT.en);
      return fallback !== undefined && fallback !== null ? fallback : path;
    },
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggleLang, t, pathFor }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
